import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Calendar, LayoutList, Eye, CheckCircle, XCircle, LogIn, Search, Filter } from 'lucide-react';
import {
  GET_RESERVAS,
  CONFIRMAR_RESERVA,
  CANCELAR_RESERVA
} from '../graphql/reservas';
import { GET_EVENTOS_CALENDARIO } from '../graphql/calendario';
import Loading from '../components/shared/Loading';
import Badge from '../components/shared/Badge';
import Button from '../components/shared/Button';
import ReservaModal from '../components/reservas/ReservaModal';
import ReservaDetalleModal from '../components/reservas/ReservaDetalleModal';
import CalendarioTimeline from '../components/reservas/CalendarioTimeline';
import ConfirmModal from '../components/shared/ConfirmModal';
import InputModal from '../components/shared/InputModal';
import SuccessModal from '../components/shared/SuccessModal';
import './Reservas.css';

function Reservas() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectionData, setSelectionData] = useState([]);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [reservaSeleccionada, setReservaSeleccionada] = useState(null);
  const [vistaActual, setVistaActual] = useState('calendario'); // 'calendario' o 'lista'

  // Estados para modales de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState({ type: 'success', title: '', message: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [filtros, setFiltros] = useState({ busqueda: '', estado: 'todos' });

  const { data, loading, error, refetch } = useQuery(GET_RESERVAS, {
    pollInterval: isProcessing ? 0 : 30000,
    fetchPolicy: 'cache-and-network'
  });

  // Query para eventos del calendario (reservas + hospedajes walk-in)
  const { data: eventosData, refetch: refetchEventos } = useQuery(GET_EVENTOS_CALENDARIO, {
    pollInterval: isProcessing ? 0 : 30000,
    fetchPolicy: 'cache-and-network'
  });

  const [confirmarReserva] = useMutation(CONFIRMAR_RESERVA);
  const [cancelarReserva] = useMutation(CANCELAR_RESERVA);

  if (loading && !data) return <Loading fullScreen />;
  if (error && !data) return <div className="error-message">Error al cargar reservas: {error.message}</div>;

  const reservas = data?.reservas || [];
  const eventosCalendario = eventosData?.eventosCalendario || [];

  // Filtrado client-side de reservas
  const reservasFiltradas = reservas.filter(r => {
    if (filtros.estado !== 'todos' && r.estado !== filtros.estado) return false;
    if (filtros.busqueda) {
      const b = filtros.busqueda.toLowerCase();
      return (
        r.codigo?.toLowerCase().includes(b) ||
        r.huesped?.nombre_completo?.toLowerCase().includes(b) ||
        r.huesped?.numero_documento?.toLowerCase().includes(b) ||
        r.habitacion?.numero?.toLowerCase().includes(b) ||
        r.habitacion?.tipo?.toLowerCase().includes(b)
      );
    }
    return true;
  });

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
  };

  const handleConfirmarReserva = (reservaId) => {
    // Encontrar la reserva para obtener su código
    const reserva = reservas.find(r => r.id === reservaId);
    const codigoReserva = reserva?.codigo || reservaId;

    // Guardar la acción de confirmación
    setConfirmAction({
      type: 'confirmar',
      reservaId,
      codigoReserva,
      title: 'Confirmar Reserva',
      message: `¿Estás seguro de confirmar la reserva ${codigoReserva}?`,
      variant: 'success'
    });

    // Mostrar modal de confirmación
    setShowConfirmModal(true);
  };

  const ejecutarConfirmacion = async () => {
    if (!confirmAction) return;

    setIsProcessing(true);

    try {
      // Ejecutar mutation CONFIRMAR_RESERVA
      await confirmarReserva({
        variables: { id: parseInt(confirmAction.reservaId, 10) }
      });

      // Cerrar modal de confirmación
      setShowConfirmModal(false);

      // Refrescar datos - forzar actualización
      const { data: nuevosDatos } = await refetch();
      await refetchEventos();

      // Log para debugging
      console.log('✅ Reserva confirmada - Refetch completado:', nuevosDatos);

      // Mostrar mensaje de éxito
      setSuccessMessage({
        type: 'success',
        title: 'Reserva Confirmada',
        message: `La reserva ${confirmAction.codigoReserva} ha sido confirmada exitosamente`
      });
      setShowSuccessModal(true);

    } catch (error) {
      // Cerrar modal de confirmación
      setShowConfirmModal(false);

      // Manejar errores apropiadamente
      console.error('Error al confirmar reserva:', error);
      setSuccessMessage({
        type: 'error',
        title: 'Error al Confirmar',
        message: error.message || 'No se pudo confirmar la reserva'
      });
      setShowSuccessModal(true);
    } finally {
      setIsProcessing(false);
      setConfirmAction(null);
    }
  };

  const handleCancelarReserva = (reservaId) => {
    // Encontrar la reserva para obtener su código
    const reserva = reservas.find(r => r.id === reservaId);
    const codigoReserva = reserva?.codigo || reservaId;

    // Guardar datos para la cancelación
    setConfirmAction({
      type: 'cancelar',
      reservaId,
      codigoReserva
    });

    // Mostrar modal para pedir motivo
    setShowInputModal(true);
  };

  const handleMotivoIngresado = (motivo) => {
    // Capturar datos antes de cerrar el modal de input
    const currentAction = confirmAction;

    // Cerrar modal de input
    setShowInputModal(false);

    // Actualizar confirmAction con el motivo
    setConfirmAction({
      ...currentAction,
      motivo,
      title: 'Cancelar Reserva',
      message: `¿Estás seguro de cancelar la reserva ${currentAction?.codigoReserva || ''}?`,
      variant: 'danger'
    });

    // Mostrar modal de confirmación
    setShowConfirmModal(true);
  };

  const ejecutarCancelacion = async () => {
    if (!confirmAction || confirmAction.type !== 'cancelar') return;

    setIsProcessing(true);

    try {
      // Ejecutar mutation CANCELAR_RESERVA
      await cancelarReserva({
        variables: {
          id: parseInt(confirmAction.reservaId, 10),
          motivo: confirmAction.motivo || undefined
        }
      });

      // Cerrar modal de confirmación
      setShowConfirmModal(false);

      // Refrescar datos - forzar actualización
      const { data: nuevosDatos } = await refetch();
      await refetchEventos();

      // Log para debugging
      console.log('❌ Reserva cancelada - Refetch completado:', nuevosDatos);

      // Mostrar mensaje de éxito
      setSuccessMessage({
        type: 'success',
        title: 'Reserva Cancelada',
        message: `La reserva ${confirmAction.codigoReserva} ha sido cancelada exitosamente`
      });
      setShowSuccessModal(true);

    } catch (error) {
      // Cerrar modal de confirmación
      setShowConfirmModal(false);

      // Manejar errores apropiadamente
      console.error('Error al cancelar reserva:', error);
      setSuccessMessage({
        type: 'error',
        title: 'Error al Cancelar',
        message: error.message || 'No se pudo cancelar la reserva'
      });
      setShowSuccessModal(true);
    } finally {
      setIsProcessing(false);
      setConfirmAction(null);
    }
  };

  const handleConfirmarAccion = () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'confirmar') {
      ejecutarConfirmacion();
    } else if (confirmAction.type === 'cancelar') {
      ejecutarCancelacion();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getEstadoBadgeVariant = (estado) => {
    const variants = {
      pendiente: 'warning',
      confirmada: 'success',
      cancelada: 'danger',
      en_curso: 'info',
      finalizada: 'secondary',
      no_show: 'danger'
    };
    return variants[estado] || 'default';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      pendiente: 'Pendiente',
      confirmada: 'Confirmada',
      cancelada: 'Cancelada',
      en_curso: 'En Curso',
      finalizada: 'Finalizada',
      no_show: 'No Show'
    };
    return labels[estado] || estado;
  };

  const handleSelectionComplete = (data) => {
    setSelectionData(data);
    setShowModal(true);
  };

  const handleVerDetalles = (reserva) => {
    setReservaSeleccionada(reserva);
    setShowDetalleModal(true);
  };

  const handleCerrarDetalleModal = () => {
    setShowDetalleModal(false);
    setReservaSeleccionada(null);
  };

  const handleConfirmarDesdeDetalle = async (reserva) => {
    await handleConfirmarReserva(reserva.id);
    handleCerrarDetalleModal();
  };

  const handleCancelarDesdeDetalle = async (reserva) => {
    await handleCancelarReserva(reserva.id);
    handleCerrarDetalleModal();
  };

  return (
    <div className="reservas-container">
        <nav className="reservas-tabs" role="tablist">
          <button
            className={`reservas-tab${vistaActual === 'calendario' ? ' reservas-tab--active' : ''}`}
            onClick={() => setVistaActual('calendario')}
            role="tab"
            aria-selected={vistaActual === 'calendario'}
          >
            <Calendar size={16} aria-hidden="true" />
            RESERVAS
          </button>
          <button
            className={`reservas-tab${vistaActual === 'lista' ? ' reservas-tab--active' : ''}`}
            onClick={() => setVistaActual('lista')}
            role="tab"
            aria-selected={vistaActual === 'lista'}
          >
            <LayoutList size={16} aria-hidden="true" />
            LISTADO DE RESERVAS
          </button>
        </nav>

        {vistaActual === 'calendario' ? (
          <CalendarioTimeline
            onSelectionComplete={handleSelectionComplete}
            eventos={eventosCalendario}
            onReservaClick={handleVerDetalles}
            onHospedajeClick={(evento) => navigate(`/hospedajes?id=${evento.id}`)}
            refetchEventos={refetchEventos}
            onNuevaReserva={() => {
              setSelectionData([]);
              setShowModal(true);
            }}
          />
        ) : (
          <>
            <div className="reservas-filtros">
              <div className="filtro-busqueda">
                <Search size={18} />
                <input
                  type="text"
                  name="busqueda"
                  placeholder="Buscar por código, huésped, documento o habitación..."
                  value={filtros.busqueda}
                  onChange={handleFiltroChange}
                />
              </div>
              <div className="filtro-estado">
                <Filter size={18} />
                <select name="estado" value={filtros.estado} onChange={handleFiltroChange}>
                  <option value="todos">Todos los estados</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="confirmada">Confirmada</option>
                  <option value="en_curso">En Curso</option>
                  <option value="finalizada">Finalizada</option>
                  <option value="cancelada">Cancelada</option>
                  <option value="no_show">No Show</option>
                </select>
              </div>
            </div>
            <div className="reservas-stats">
              <span className="stats-text">
                Mostrando {reservasFiltradas.length} de {reservas.length} {reservas.length === 1 ? 'reserva' : 'reservas'}
              </span>
            </div>

            {reservasFiltradas.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">{filtros.busqueda || filtros.estado !== 'todos' ? '🔍' : '📋'}</div>
                <h3>{filtros.busqueda || filtros.estado !== 'todos'
                  ? 'No se encontraron resultados'
                  : 'No se encontraron reservas'}</h3>
                <p>{filtros.busqueda || filtros.estado !== 'todos'
                  ? 'Intenta con otros criterios de búsqueda'
                  : 'Comienza creando tu primera reserva.'}</p>
              </div>
            ) : (
              <div className="reservas-tabla-container">
                <table className="reservas-tabla">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Huésped</th>
                      <th>Habitación</th>
                      <th>Check-in</th>
                      <th>Check-out</th>
                      <th className="col-center col-noches">Noches</th>
                      <th>Total</th>
                      <th>Estado</th>
                      <th className="col-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservasFiltradas.map((reserva) => (
                      <tr key={reserva.id} className="reservas-tabla__row">
                        <td className="td-codigo">{reserva.codigo}</td>
                        <td>{reserva.huesped?.nombre_completo || '—'}</td>
                        <td>{reserva.habitacion?.numero} - {reserva.habitacion?.tipo}</td>
                        <td>{formatDate(reserva.fecha_entrada)}</td>
                        <td>{formatDate(reserva.fecha_salida)}</td>
                        <td className="col-center col-noches">{reserva.noches}</td>
                        <td className="td-total">{formatPrice(reserva.precio_total)}</td>
                        <td>
                          <Badge variant={getEstadoBadgeVariant(reserva.estado)}>
                            {getEstadoLabel(reserva.estado)}
                          </Badge>
                        </td>
                        <td>
                          <div className="tabla-acciones">
                            <button
                              className="tabla-btn tabla-btn--info"
                              title="Ver Detalles"
                              onClick={() => handleVerDetalles(reserva)}
                            >
                              <Eye size={16} />
                            </button>
                            {reserva.estado === 'pendiente' && (
                              <>
                                <button
                                  className="tabla-btn tabla-btn--success"
                                  title="Confirmar Reserva"
                                  onClick={() => handleConfirmarReserva(reserva.id)}
                                >
                                  <CheckCircle size={16} />
                                </button>
                                <button
                                  className="tabla-btn tabla-btn--danger"
                                  title="Cancelar Reserva"
                                  onClick={() => handleCancelarReserva(reserva.id)}
                                >
                                  <XCircle size={16} />
                                </button>
                              </>
                            )}
                            {reserva.estado === 'confirmada' && (
                              <>
                                <button
                                  className="tabla-btn tabla-btn--primary"
                                  title="Check-In"
                                  onClick={() => navigate(`/hospedajes/checkin?reserva=${reserva.id}`)}
                                >
                                  <LogIn size={16} />
                                </button>
                                <button
                                  className="tabla-btn tabla-btn--danger"
                                  title="Cancelar Reserva"
                                  onClick={() => handleCancelarReserva(reserva.id)}
                                >
                                  <XCircle size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Modal de Nueva Reserva */}
        <ReservaModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectionData([]);
          }}
          selectionData={selectionData}
          onSuccess={() => {
            refetch();
            refetchEventos();
            setShowModal(false);
            setSelectionData([]);
          }}
        />

        {/* Modal de Detalles de Reserva */}
        <ReservaDetalleModal
          isOpen={showDetalleModal}
          onClose={handleCerrarDetalleModal}
          reserva={reservaSeleccionada}
          onConfirmar={handleConfirmarDesdeDetalle}
          onCancelar={handleCancelarDesdeDetalle}
          onUpdated={() => {
            refetch();
            refetchEventos();
            handleCerrarDetalleModal();
          }}
        />

        {/* Modal de Confirmación */}
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setConfirmAction(null);
          }}
          onConfirm={handleConfirmarAccion}
          title={confirmAction?.title}
          message={confirmAction?.message}
          variant={confirmAction?.variant || 'warning'}
          loading={isProcessing}
        />

        {/* Modal para Motivo de Cancelación */}
        <InputModal
          isOpen={showInputModal}
          onClose={() => {
            setShowInputModal(false);
            setConfirmAction(null);
          }}
          onConfirm={handleMotivoIngresado}
          title="Motivo de Cancelación"
          message="Por favor, ingrese el motivo de la cancelación de la reserva:"
          placeholder="Ej: Cliente canceló, error en la reserva, etc."
          required={false}
        />

        {/* Modal de Éxito/Error */}
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          title={successMessage.title}
          message={successMessage.message}
          type={successMessage.type}
        />
      </div>
  );
}

export default Reservas;
